import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Globe from "react-globe.gl";
import * as topojson from "topojson-client";
import * as THREE from "three";
import { ChevronLeft, Plus, Minus, RotateCw, Crosshair, Map as MapIcon } from "lucide-react";
import OperationAtlas from "@/components/OperationAtlas";
import ArcadeLeaderboard from "@/components/ArcadeLeaderboard";

// Holographic radar palette
const COLORS = {
  bg: "#020911",
  ocean: "#020911",
  countryFill: "rgba(14, 40, 78, 0.6)",
  countryStroke: "rgba(102, 230, 255, 0.55)",
  countryStrokeBright: "rgba(102, 230, 255, 0.9)",
  hover: "#39FF14",
  hoverGlow: "rgba(57, 255, 20, 0.6)",
  grid: "rgba(102, 230, 255, 0.25)",
  atmosphere: "#3aa0ff",
};

export default function World() {
  const globeRef = useRef(null);
  const wrapRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dims, setDims] = useState({ w: 800, h: 560 });
  const [mode, setMode] = useState("map"); // "map" | "atlas"

  // Real Three.js material so the Globe can dispose() it cleanly on unmount.
  const globeMaterial = useMemo(
    () => new THREE.MeshPhongMaterial({ color: COLORS.ocean, opacity: 0.92, transparent: true }),
    []
  );
  useEffect(() => () => globeMaterial.dispose?.(), [globeMaterial]);

  // Load country polygons (110m resolution — ~250KB, fast)
  useEffect(() => {
    let cancelled = false;
    fetch("https://unpkg.com/world-atlas@2.0.2/countries-110m.json")
      .then(r => r.json())
      .then(topo => {
        if (cancelled) return;
        const feats = topojson.feature(topo, topo.objects.countries).features;
        setCountries(feats);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Responsive sizing
  useEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const w = el.clientWidth;
      const h = Math.max(420, Math.min(720, Math.round(w * 0.72)));
      setDims({ w, h });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Initial camera + auto-rotate (map mode defaults)
  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    g.pointOfView({ lat: 20, lng: 15, altitude: 2.4 }, 0);
    const controls = g.controls();
    if (controls) {
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.35;
      controls.enableZoom = true;
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
    }
  }, [countries.length]);

  // Mode-aware control tuning:
  // In Atlas mode the globe must be STATIC (no idle rotation) and less
  // twitchy so players can precisely hunt small nations under the 10-second
  // per-round timer. Restore defaults when returning to map mode.
  //
  // react-globe.gl re-derives rotateSpeed / zoomSpeed from camera altitude on
  // EVERY animation frame, so a one-shot assignment is silently overwritten.
  // We hold our target values via a rAF loop while atlas mode is active.
  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    const controls = g.controls();
    if (!controls) return;

    // Static one-shot properties (these stick — the library doesn't touch them).
    if (mode === "atlas") {
      controls.autoRotate = false;
      controls.enableDamping = true;
      controls.dampingFactor = 0.14;      // smoother lerp for zoom easing
    } else {
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.35;
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
    }

    if (mode !== "atlas") return;

    // Sustained re-apply loop for atlas mode. Runs at ~60fps but is cheap:
    // two number assignments per tick. Cancelled on mode change / unmount.
    // Values chosen to reduce sensitivity ~45% vs. library defaults.
    const ATLAS_ROTATE_SPEED = 0.55;
    const ATLAS_ZOOM_SPEED   = 0.45;
    let rafId;
    const enforce = () => {
      const c = g.controls?.();
      if (c) {
        c.rotateSpeed = ATLAS_ROTATE_SPEED;
        c.zoomSpeed   = ATLAS_ZOOM_SPEED;
      }
      rafId = requestAnimationFrame(enforce);
    };
    rafId = requestAnimationFrame(enforce);
    return () => cancelAnimationFrame(rafId);
  }, [mode]);

  const polygonCapColor = useMemo(() => (d) => {
    if (d === hovered || d === selected) return COLORS.hover;
    return COLORS.countryFill;
  }, [hovered, selected]);

  const polygonSideColor = useMemo(() => (d) => {
    if (d === hovered || d === selected) return "rgba(57, 255, 20, 0.35)";
    return "rgba(58, 160, 255, 0.12)";
  }, [hovered, selected]);

  const polygonStrokeColor = useMemo(() => (d) => {
    if (d === hovered || d === selected) return "#39FF14";
    return COLORS.countryStroke;
  }, [hovered, selected]);

  const polygonAltitude = useMemo(() => (d) => {
    if (d === hovered || d === selected) return 0.025;
    return 0.008;
  }, [hovered, selected]);

  const polygonLabel = (d) => {
    const name = d?.properties?.name || "Region";
    return `<div style="font-family: 'Cinzel', serif; padding: 8px 12px; background: rgba(2,9,17,0.95); border: 1px solid #39FF14; box-shadow: 0 0 20px rgba(57,255,20,0.4); color: #39FF14; font-size: 13px; letter-spacing: 0.12em; text-transform: uppercase;">${name}</div>`;
  };

  const zoomBy = (factor) => {
    const g = globeRef.current;
    if (!g) return;
    const pov = g.pointOfView();
    g.pointOfView({ ...pov, altitude: Math.max(0.6, Math.min(4.5, pov.altitude * factor)) }, 350);
  };

  const resetView = () => {
    const g = globeRef.current;
    if (!g) return;
    g.pointOfView({ lat: 20, lng: 15, altitude: 2.4 }, 800);
    setSelected(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="world-page">
      <Link to="/wiki" className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-4" data-testid="world-back">
        <ChevronLeft className="w-3 h-3" /> Wiki
      </Link>

      <div className="flex items-end justify-between gap-4 mb-5 md:mb-6 flex-wrap">
        <div>
          <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Tactical Map</span>
          <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase leading-tight">World</h1>
        </div>

        {/* Mode selector — Operation Atlas is now the loud primary CTA */}
        <div className="flex items-center gap-2 md:gap-3" data-testid="world-mode-selector">
          <button
            onClick={() => setMode("map")}
            className={`px-3 py-2 inline-flex items-center gap-1.5 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.18em] border transition ${mode==="map" ? "bg-[var(--t-blue)]/15 text-[var(--t-cyan)] border-[var(--t-blue)]" : "text-[var(--t-text-dim)] border-[var(--t-border)] hover:text-[var(--t-text)]"}`}
            data-testid="world-mode-map"
          >
            <MapIcon className="w-3.5 h-3.5" /> Tactical Map
          </button>
          <button
            onClick={() => setMode("atlas")}
            className={`relative px-4 md:px-5 py-2 md:py-2.5 inline-flex items-center gap-2 font-display text-sm md:text-base uppercase tracking-[0.12em] border-2 transition group ${mode==="atlas" ? "bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]" : "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14] hover:bg-[#39FF14]/20"}`}
            style={{ boxShadow: mode==="atlas" ? "0 0 20px rgba(57,255,20,0.55), inset 0 0 12px rgba(57,255,20,0.20)" : "0 0 16px rgba(57,255,20,0.40)" }}
            data-testid="world-mode-atlas"
          >
            <span className="absolute -top-2 -right-2 bg-[#39FF14] text-[#02091e] font-mono text-[8px] tracking-[0.2em] px-1.5 py-0.5 rounded-sm shadow-[0_0_10px_#39FF14]">NEW</span>
            <Crosshair className="w-4 h-4 md:w-5 md:h-5 group-hover:rotate-90 transition-transform duration-500" />
            <span style={{ textShadow: "0 0 12px rgba(57,255,20,0.85)" }}>Play Atlas</span>
            <span className="hidden md:inline w-1.5 h-1.5 rounded-full bg-[#39FF14] pulse-glow ml-1" />
          </button>
        </div>
      </div>

      {/* Spotlight banner promoting Atlas when in map mode */}
      {mode === "map" && (
        <div
          className="panel corner-frame relative overflow-hidden mb-5 md:mb-6 cursor-pointer group"
          onClick={() => setMode("atlas")}
          style={{ borderColor: "#39FF14", boxShadow: "0 0 24px rgba(57,255,20,0.22), inset 0 0 14px rgba(57,255,20,0.06)" }}
          data-testid="atlas-banner"
        >
          <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[radial-gradient(circle,_rgba(57,255,20,0.22)_0%,_transparent_70%)] pointer-events-none" />
          <div className="relative flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-3 md:py-4">
            <div className="flex items-center gap-3 md:gap-4 min-w-0">
              <span className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 border-2 border-[#39FF14] shrink-0" style={{ boxShadow: "0 0 14px #39FF14, inset 0 0 8px rgba(57,255,20,0.25)" }}>
                <Crosshair className="w-5 h-5 md:w-6 md:h-6 text-[#39FF14]" />
              </span>
              <div className="min-w-0">
                <div className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] text-[#39FF14] uppercase mb-0.5">// Territoria Arcade · New</div>
                <div className="font-display text-base md:text-xl text-[var(--t-text)] uppercase leading-tight">
                  Operation Atlas · <span className="text-[#39FF14]" style={{ textShadow: "0 0 12px #39FF14" }}>90-second geo-recon</span>
                </div>
              </div>
            </div>
            <span className="font-mono text-[10px] md:text-xs tracking-[0.2em] text-[#39FF14] uppercase group-hover:translate-x-1 transition-transform">
              ▶ Play Now
            </span>
          </div>
        </div>
      )}

      <div className="panel p-4 md:p-5 mb-6 border-l-2 !border-l-[#39FF14]" data-testid="world-placeholder">
        <div className="flex items-start gap-3">
          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#39FF14] pulse-glow flex-shrink-0" />
          <p className="text-sm md:text-base text-[var(--t-text-dim)] leading-relaxed">
            <span className="font-mono text-xs tracking-[0.2em] text-[#39FF14] uppercase">// transmission_pending</span><br />
            Once the game is live, this will be a live feed of Territoria regions.
          </p>
        </div>
      </div>

      {/* Globe container */}
      <div
        ref={wrapRef}
        className="relative panel panel-glow corner-frame overflow-hidden"
        style={{
          background: `radial-gradient(ellipse at center, #051428 0%, ${COLORS.bg} 70%)`,
          height: dims.h,
        }}
        data-testid="world-globe"
      >
        {/* Top frame strip */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-3 md:px-4 py-2 bg-[#02091199] backdrop-blur-sm border-b border-[rgba(102,230,255,0.18)]">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-glow" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-cyan)] uppercase">tactical_link · 195 nations</span>
          </div>
          {hovered && (
            <span className="font-mono text-[10px] md:text-xs tracking-[0.18em] text-[#39FF14] uppercase truncate max-w-[50%]" data-testid="hover-label">
              ► {hovered?.properties?.name}
            </span>
          )}
        </div>

        {/* Globe */}
        <div className="absolute inset-0 flex items-center justify-center">
          <Globe
            ref={globeRef}
            width={dims.w}
            height={dims.h}
            backgroundColor={"rgba(0,0,0,0)"}
            globeImageUrl={null}
            showGlobe={true}
            globeMaterial={globeMaterial}
            showAtmosphere={true}
            atmosphereColor={COLORS.atmosphere}
            atmosphereAltitude={0.18}
            showGraticules={true}
            polygonsData={countries}
            polygonGeoJsonGeometry={(d) => d.geometry}
            polygonCapColor={polygonCapColor}
            polygonSideColor={polygonSideColor}
            polygonStrokeColor={polygonStrokeColor}
            polygonAltitude={polygonAltitude}
            polygonLabel={polygonLabel}
            onPolygonHover={setHovered}
            onPolygonClick={(d) => {
              if (mode === "atlas") {
                window.dispatchEvent(new CustomEvent("atlas-globe-click", { detail: { feature: d } }));
              } else {
                setSelected(s => s === d ? null : d);
              }
            }}
            polygonsTransitionDuration={250}
            animateIn={true}
          />
        </div>

        {/* OPERATION ATLAS — overlays game UI inside the globe panel */}
        {mode === "atlas" && (
          <OperationAtlas
            features={countries}
            globeRef={globeRef}
            onExit={() => setMode("map")}
          />
        )}

        {/* Zoom controls */}
        <div className="absolute top-12 right-3 md:top-14 md:right-4 z-10 flex flex-col gap-1.5" data-testid="zoom-controls">
          <button
            onClick={() => zoomBy(0.7)}
            className="w-9 h-9 inline-flex items-center justify-center bg-[#02091199] border border-[rgba(102,230,255,0.35)] hover:border-[#39FF14] text-[var(--t-cyan)] hover:text-[#39FF14] backdrop-blur-sm transition"
            data-testid="zoom-in"
            aria-label="Zoom in"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={() => zoomBy(1.4)}
            className="w-9 h-9 inline-flex items-center justify-center bg-[#02091199] border border-[rgba(102,230,255,0.35)] hover:border-[#39FF14] text-[var(--t-cyan)] hover:text-[#39FF14] backdrop-blur-sm transition"
            data-testid="zoom-out"
            aria-label="Zoom out"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            onClick={resetView}
            className="w-9 h-9 inline-flex items-center justify-center bg-[#02091199] border border-[rgba(102,230,255,0.35)] hover:border-[#39FF14] text-[var(--t-cyan)] hover:text-[#39FF14] backdrop-blur-sm transition"
            data-testid="reset-view"
            aria-label="Reset view"
          >
            <RotateCw className="w-4 h-4" />
          </button>
        </div>

        {/* Bottom info strip */}
        <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-between px-3 md:px-4 py-2 bg-[#02091199] backdrop-blur-sm border-t border-[rgba(102,230,255,0.18)]">
          <span className="font-mono text-[9px] md:text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">drag to rotate · scroll to zoom · click region for intel</span>
          <span className="hidden md:inline font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">v0.0.001</span>
        </div>
      </div>

      {/* Selected country details */}
      {selected && mode === "map" && (
        <div className="panel panel-glow p-5 md:p-6 mt-5 border-l-2 !border-l-[#39FF14]" data-testid="selected-info">
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#39FF14] uppercase mb-2">// selected region</div>
          <h2 className="font-display text-2xl md:text-3xl text-[#39FF14] uppercase tracking-[0.04em]" style={{ textShadow: "0 0 18px rgba(57,255,20,0.5)" }}>
            {selected?.properties?.name}
          </h2>
          <p className="text-sm text-[var(--t-text-dim)] mt-3">Live region dossier — government, treasury, alliances, and battles — unlocks when the Territoria engine connects.</p>
          <button onClick={() => setSelected(null)} className="btn-ghost !py-1.5 !px-3 !text-xs mt-4" data-testid="deselect">Deselect</button>
        </div>
      )}

      {/* Operation Atlas — community recon stats + leaderboard */}
      <ArcadeLeaderboard />
    </div>
  );
}
